import React, { useState } from 'react';

const ShippingAddressForm = ({ onAddressChange, initialAddress = {} }) => {
  const [address, setAddress] = useState({
    firstName: initialAddress.firstName || '',
    lastName: initialAddress.lastName || '',
    addressLine1: initialAddress.addressLine1 || '',
    addressLine2: initialAddress.addressLine2 || '',
    city: initialAddress.city || '',
    state: initialAddress.state || '',
    pincode: initialAddress.pincode || '',
    country: initialAddress.country || 'India',
    phone: initialAddress.phone || ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    const updatedAddress = { ...address, [field]: value };
    setAddress(updatedAddress);

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Validate and notify parent
    const validationErrors = validateAddress(updatedAddress);
    setErrors(validationErrors);

    // Only call onAddressChange if address is valid
    if (Object.keys(validationErrors).length === 0) {
      onAddressChange(updatedAddress);
    } else {
      onAddressChange(null); // Invalid address
    }
  };

  const validateAddress = (addressData) => {
    const errors = {};

    if (!addressData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!addressData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!addressData.addressLine1.trim()) {
      errors.addressLine1 = 'Address is required';
    }

    if (!addressData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!addressData.state.trim()) {
      errors.state = 'State is required';
    }

    if (!addressData.pincode.trim()) {
      errors.pincode = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(addressData.pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    if (!addressData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(addressData.phone.replace(/\s+/g, ''))) {
      errors.phone = 'Phone number must be 10 digits';
    }

    return errors;
  };

  // Check if address is valid
  const isAddressValid = Object.keys(errors).length === 0 &&
    address.firstName && address.lastName && address.addressLine1 &&
    address.city && address.state && address.pincode && address.phone;

  return (
    <div className="bg-gray-50 rounded-2xl p-4 sm:p-6 border border-gray-200">
      <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center">
        <svg className="w-5 h-5 mr-2 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        Shipping Address
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* First Name */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            First Name *
          </label>
          <input
            type="text"
            value={address.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Enter first name"
          />
          {errors.firstName && (
            <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Last Name *
          </label>
          <input
            type="text"
            value={address.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Enter last name"
          />
          {errors.lastName && (
            <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
          )}
        </div>

        {/* Address Line 1 */}
        <div className="sm:col-span-2">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Address Line 1 *
          </label>
          <input
            type="text"
            value={address.addressLine1}
            onChange={(e) => handleInputChange('addressLine1', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.addressLine1 ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="House/Flat No., Building Name, Street"
          />
          {errors.addressLine1 && (
            <p className="text-red-500 text-xs mt-1">{errors.addressLine1}</p>
          )}
        </div>

        {/* Address Line 2 */}
        <div className="sm:col-span-2">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Address Line 2
          </label>
          <input
            type="text"
            value={address.addressLine2}
            onChange={(e) => handleInputChange('addressLine2', e.target.value)}
            className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Landmark, Area (Optional)"
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            City *
          </label>
          <input
            type="text"
            value={address.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Enter city"
          />
          {errors.city && (
            <p className="text-red-500 text-xs mt-1">{errors.city}</p>
          )}
        </div>

        {/* State */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            State *
          </label>
          <select
            value={address.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
          >
            <option value="">Select State</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
            <option value="Assam">Assam</option>
            <option value="Bihar">Bihar</option>
            <option value="Chhattisgarh">Chhattisgarh</option>
            <option value="Goa">Goa</option>
            <option value="Gujarat">Gujarat</option>
            <option value="Haryana">Haryana</option>
            <option value="Himachal Pradesh">Himachal Pradesh</option>
            <option value="Jharkhand">Jharkhand</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Kerala">Kerala</option>
            <option value="Madhya Pradesh">Madhya Pradesh</option>
            <option value="Maharashtra">Maharashtra</option>
            <option value="Manipur">Manipur</option>
            <option value="Meghalaya">Meghalaya</option>
            <option value="Mizoram">Mizoram</option>
            <option value="Nagaland">Nagaland</option>
            <option value="Odisha">Odisha</option>
            <option value="Punjab">Punjab</option>
            <option value="Rajasthan">Rajasthan</option>
            <option value="Sikkim">Sikkim</option>
            <option value="Tamil Nadu">Tamil Nadu</option>
            <option value="Telangana">Telangana</option>
            <option value="Tripura">Tripura</option>
            <option value="Uttar Pradesh">Uttar Pradesh</option>
            <option value="Uttarakhand">Uttarakhand</option>
            <option value="West Bengal">West Bengal</option>
            <option value="Delhi">Delhi</option>
            <option value="Puducherry">Puducherry</option>
          </select>
          {errors.state && (
            <p className="text-red-500 text-xs mt-1">{errors.state}</p>
          )}
        </div>

        {/* Pincode */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Pincode *
          </label>
          <input
            type="text"
            value={address.pincode}
            onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.pincode ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="6-digit pincode"
            maxLength="6"
          />
          {errors.pincode && (
            <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            value={address.phone}
            onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="10-digit mobile number"
            maxLength="10"
          />
          {errors.phone && (
            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
          )}
        </div>
      </div>

      {/* Address Validation Status */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-xl bg-gray-100">
        <div className="flex items-center">
          {isAddressValid ? (
            <>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span className="text-[#008000] text-xs sm:text-sm font-medium">Address is complete and valid</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <span className="text-orange-700 text-xs sm:text-sm font-medium">Please complete all required fields</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShippingAddressForm;
